import React, { useContext, useEffect, useMemo, useState } from "react";
import "./BeachRecTourism.scss";
import { MapContext } from "../../../context/MapContext";
import GeoSelector from "../../GeoSelector/GeoSelector";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/tasks/support/Query";
import GeoScale from "../../GeoScale/GeoScale";
import {
  Beaches,
  refLayersToInclude,
  ReferenceConfig,
} from "./BeachRecTourismConfig";
import BeachRecStats from "./BeachRecStats/BeachRecStats";
import BeachRecChart from "./BeachRecChart/BeachRecChart";
import useQueryString from "../../../hooks/useQueryString";
import ReferenceLayers from "../../ReferenceLayers/ReferenceLayers";
import utils from "../../../helpers/utils";
import References from "../../References/References";

function BeachRecTourism() {
  const [mapObject] = useContext(MapContext);
  const [selectedGeometry, setSelectedGeometry] = useQueryString(
    "selectedGeometry",
    "CROP Countries"
  );
  const [refLayersEnabled, setRefLayersEnabled] = useQueryString(
    "refLayers",
    []
  );

  const [isGeoScaled, setIsGeoScaled] = useQueryString("isGeoScaled", false);
  const [selectedStat, setSelectedStat] = useQueryString(
    "selectedStat",
    !refLayersEnabled || !refLayersEnabled.length ? "Expenditures (USD)" : null
  );
  const [stats, setStats] = useState({});
  const [selectedChart, setSelectedChart] = useQueryString(
    "selectedChart",
    null
  );

  const geoSelections = [
    "CROP Countries",
    "Dominica",
    "Grenada",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
  ];

  /**
   * Every time this route is accessed, make sure the EEZ layer reflects the correct boundaries
   */
  useEffect(() => {
    const eez = mapObject?.map.findLayerById("eez");
    const countries = Beaches.data.map((c) => c.name);

    if (eez) {
      eez.definitionExpression = `Dissolve in (${countries
        .map((c) => "'" + utils.getCountryForEEZ(c) + "'")
        .join(",")})`;
    }
  }, [mapObject]);

  /**
   * Function that looks up layer object from config file to use based on current
   * app selections (stat, chart status, reefs vs beaches, and eventually selected geometry)
   */
  const getLayerForSelections = (stat, chart, geometry, isScaled) => {
    const dataSet = Beaches.data;
    let layersToSearch;
    if (isScaled) {
      layersToSearch = dataSet.find(
        (country) => country.name === geometry
      ).layers;
    } else {
      layersToSearch = dataSet.find(
        (country) => country.name === "CROP Countries"
      ).layers;
    }

    let dataLayer = layersToSearch.find(
      (crt) =>
        crt.name === "Estimated expenditure from beaches linked to natural values (US$)"
    );
    if (stat === "Expenditures (USD)") {
      if (!chart) {
        dataLayer = layersToSearch.find(
          (crt) =>
            crt.name === "Estimated expenditure from beaches linked to natural values (US$)"
        );
      } else if (chart === "Overnight") {
        dataLayer = layersToSearch.find(
          (crt) => crt.name === "Overnight Expenditure"
        );
      } else {
        dataLayer = layersToSearch.find(
          (crt) => crt.name === "Cruise Expenditure"
        );
      }
    }
    if (
      stat === "Total visitation value (# of people)" ||
      stat === "Total visitation value (number of people)"
    ) {
      if (!chart) {
        dataLayer = layersToSearch.find(
          (crt) => crt.name === "Modelled visitation value of nature-dependent beach tourism (per ha)"
        );
      } else if (chart === "Overnight") {
        dataLayer = layersToSearch.find(
          (crt) => crt.name === "Overnight Visitation"
        );
      } else {
        dataLayer = layersToSearch.find(
          (crt) => crt.name === "Cruise Visitation"
        );
      }
    }
    return dataLayer;
  };

  /**
   * Only one model layer is active at a time. Memoize it and update when user selections change
   */
  const layer = useMemo(() => {
    if (!refLayersEnabled.length) {
      const dataLayer = getLayerForSelections(
        selectedStat,
        selectedChart,
        selectedGeometry,
        Boolean(isGeoScaled)
      );
      const featureLayer = new FeatureLayer({
        url: dataLayer.url,
        title: dataLayer.name,
      });
      if (selectedStat === "Highest value beaches (top 10%) generate (USD)") {
        featureLayer.title =
          "Modelled total dollar value of beach tourism ($USD)";
      }
      return featureLayer;
    }
  }, [
    selectedStat,
    selectedChart,
    selectedGeometry,
    isGeoScaled,
    refLayersEnabled,
  ]);

  /**
   * When the memoized layer object updates, we need to remove the current layer
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.remove(layer);
    }
  }, [layer, mapObject?.map]);

  /**
   * Second effect declared to add the new layer. Runs after first efect that removes. Has callback for removing on component unmount
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.add(layer);

      return () => {
        mapObject?.map.remove(layer);
      };
    }
  }, [mapObject?.map, layer]);

  /**
   * On component mount, fetch the stats table and prepare a query tasks to execute queries on
   */
  const queryTable = useMemo(() => {
    const beachesTable = Beaches.data
      .find((b) => b.name === "CROP Countries")
      .layers.find((b) => b.name === "Statistics Table");
    return new QueryTask({ url: beachesTable.url });
  }, []);

  /**
   * Fetch the stats for the currrent app and set them in state.
   * Currently, reefs and beaches are different "apps" in the table.
   * So, we update where clause and execute when the user toggles between
   */
  useEffect(() => {
    const query = new Query();
    query.returnGeometry = false;
    query.outFields = ["*"];
    query.where = "App = 'Nature Dependent Beaches'";

    queryTable.execute(query).then((results) => {
      const newStats = {};
      results.features.forEach((feature) => {
        newStats[feature.attributes.Statistic] = feature.attributes;
      });
      setStats(newStats);
    });
  }, [queryTable, setStats]);

  useEffect(() => {
    if (selectedStat === "Highest value beaches (top 10%) generate (USD)") {
      const renderer = {
        type: "class-breaks",
        field: "Top10_Pct",
        defaultSymbol: {
          type: "simple-fill",
          color: "grey",
          outline: {
            color: "grey",
            width: 1,
          },
        },
        defaultLabel: "All others",
        classBreakInfos: [
          {
            minValue: 0.9,
            maxValue: 1.9,
            label: "Top 10%",
            symbol: {
              type: "simple-fill",
              color: "red",
              outline: {
                color: "red",
                width: 1,
              },
            },
          },
        ],
      };
      layer.renderer = renderer;
    }
    layer?.refresh();
  }, [layer, selectedStat]);

  /**
   * Simple handler for when the user selects a new geometry. Update in state
   */
  const handleGeometryChange = (geometry) => {
    setSelectedGeometry(geometry);
    //auto-scale data if anything is selected
    setIsGeoScaled(geometry !== "CROP Countries");
  };

  /**
   * Simple handler for when the user toggle the geo scaler. Update in state
   */
  const onGeoScaleToggle = () => {
    setIsGeoScaled(!isGeoScaled);
  };

  /**
   * Simple handler for when the user clicks on a stat. Update in state
   */
  const handleStatClick = (stat) => {
    setSelectedStat(stat.name);
    setSelectedChart(null);
    setRefLayersEnabled([]);
  };

  /**
   * Simple handler for when the user clicks on a bar in the chart. Nulls out if user clicks active bar. Update in state
   */
  const handleChartClick = (elemClicked) => {
    if (elemClicked === selectedChart) {
      setSelectedChart(null);
    } else {
      setSelectedChart(elemClicked);
    }
  };

  /**
   * Toggles the visibility of a reference layer by adding/removing from an array that tracks currently visible ref layers
   */
  /**
   * single-select (radio logic)
   */
  const handleRefLayerToggle = (layer) => {
    const layersEnabled = [...refLayersEnabled];
    if (layersEnabled.includes(layer)) {
      //turn off
      layersEnabled.splice(layersEnabled.indexOf(layer), 1);
      setRefLayersEnabled(layersEnabled);
    } else {
      //turn on
      setRefLayersEnabled([layer]);
    }
    setSelectedChart(null);
    setSelectedStat(null);
  };
  /**
   * multi-select (checkbox logic)
   */
  // const handleRefLayerToggle = (layer) => {
  //   const newLayers = [...refLayersEnabled];
  //   if (refLayersEnabled.indexOf(layer) > -1) {
  //     newLayers.splice(refLayersEnabled.indexOf(layer), 1);
  //     setRefLayersEnabled(newLayers);
  //   } else {
  //     newLayers.push(layer);
  //     setRefLayersEnabled(newLayers);
  //   }
  // };

  /**
   * clear all active reference layers
   */

  return (
    <div className="coralRecTourism">
      <p className="instructions">
        Explore the values of nature-dependent beach tourism in five Eastern
        Caribbean countries.
      </p>
      <div className="geoSelect">
        <GeoSelector
          selectedGeometry={selectedGeometry}
          handleOnChange={handleGeometryChange}
          selections={geoSelections}
        />
        <GeoScale geoScaled={isGeoScaled} onToggle={onGeoScaleToggle} />
      </div>
      <div className="quickStats">
        <h3>Quick Stats for {selectedGeometry}</h3>
        <BeachRecStats
          selectedGeometry={selectedGeometry}
          selectedStat={selectedStat}
          stats={stats}
          handleStatClick={handleStatClick}
        />
      </div>
      <div className="chart">
        {Object.keys(stats).length > 0 && (
          <BeachRecChart
            stats={stats}
            selectedStat={selectedStat}
            selectedChart={selectedChart}
            handleChartClick={handleChartClick}
            selectedGeometry={selectedGeometry}
          />
        )}
      </div>
      <div className="otherMaps">
        <h3>Additional Map Layers:</h3>
        <ReferenceLayers
          layersToInclude={refLayersToInclude}
          layersEnabled={refLayersEnabled}
          isRadio
          handleLayerToggle={handleRefLayerToggle}
        />
      </div>
      {ReferenceConfig && (
        <div className="references-wrapper">
          <h3>References</h3>
          <References refs={ReferenceConfig} />
        </div>
      )}
    </div>
  );
}

export default BeachRecTourism;
